import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, DataTable, Select } from "@outpave/ds";
import type { DataColumn, SelectOption } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
// Themed primitives: the DS exports add props this table does not use (D10).
import { Button, Typography } from "@mui/material";
import {
  useAllocationTypes,
  type AllocationType,
} from "../../../hooks/useAllocationTypes";
import { GET_INTEGRATION } from "../../../graphql/integration";
import {
  useUpdateAllocationTypeSettings,
  AllocationTypeSetting,
} from "hooks/useUpdateAllocationTypeSettings";
import { useQuery } from "@apollo/client";
import { areMapsEqual } from "utils/mapObjectEqual";

interface AllocationSettingsDrawerProps {
  integrationType?: string;
  open: boolean;
  handleCloseDrawer: () => void;
  /** Where the panel puts its buttons: the host drawer's footer. */
  footerHost?: HTMLElement | null;
}

/** How the server spells an allocation type with no integration mapping. */
const NOT_MAPPED = "none";

/** The integration entity node, limited to the fields this picker reads. */
interface IntegrationEntity {
  entityKey: string;
  entityName: string;
}

export interface MappedAllocations {
  key: string;
  name: string;
  integrationEntity: string;
  editable: boolean;
}

const AllocationSettingsDrawer: React.FC<AllocationSettingsDrawerProps> = (
  props,
) => {
  const { integrationType, open, handleCloseDrawer, footerHost } = props;
  const { t } = useTranslation();
  const [originalItems, setOriginalItems] = React.useState<{
    [key: string]: AllocationTypeSetting;
  }>({});
  const [openWarningModal, setOpenWarningModal] = React.useState(false);

  const {
    refetchAllocationTypes,
    allocationTypes,
    loading: loadingTypes,
  } = useAllocationTypes({ showAll: true });
  const {
    selectedItems,
    setSelectedItems,
    update: updateAllocationTypeSettings,
    loading,
  } = useUpdateAllocationTypeSettings();

  const { data: integrationData } = useQuery(GET_INTEGRATION, {
    variables: { integrationType: integrationType },
    skip: !integrationType,
  });

  const integrationEntityList: IntegrationEntity[] =
    integrationData?.integration?.settings?.integrationEntitySettings ?? [];

  const selectedEntityKeys = React.useMemo(() => {
    return new Set(
      Object.values(selectedItems)
        .map((item) => item.integrationEntity)
        .filter(Boolean),
    );
  }, [selectedItems]);

  const resetDraft = () => {
    setSelectedItems({});
    setOriginalItems({});
  };

  // Separators off: the name is tenant data and a colon in it would
  // otherwise be read as a namespace.
  const nameOf = (allocationType: AllocationType) =>
    t(allocationType.name, { nsSeparator: false, keySeparator: false });

  const onCloseDrawer = () => {
    if (loading) return;
    handleCloseDrawer();
  };

  useEffect(() => {
    if (open) {
      refetchAllocationTypes();
      return;
    }
    resetDraft();
    setOpenWarningModal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keyed to the types, not to an empty draft: the reopen refetch is async,
  // so an empty-draft guard seeds from the previous fetch and then skips the
  // fresh one for being non-empty. Both passes land here, and a draft edited
  // between them wins, since the refetch would otherwise undo it silently.
  useEffect(() => {
    if (!open || !allocationTypes?.length) return;

    const allocationMap: Record<string, AllocationTypeSetting> =
      Object.fromEntries(allocationTypes.map((a) => [a.key, a]));
    const edited =
      Object.keys(selectedItems).length > 0 &&
      !areMapsEqual(originalItems, selectedItems);

    setOriginalItems(allocationMap);
    if (!edited) setSelectedItems(allocationMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allocationTypes]);

  const footer = (
    <>
      <Button
        variant="primary-outlined"
        disabled={loading}
        onClick={onCloseDrawer}
      >
        {t("Cancel")}
      </Button>
      <Button
        variant="primary-filled"
        disabled={loading || areMapsEqual(originalItems, selectedItems)}
        onClick={async () => {
          setOpenWarningModal(true);
        }}
      >
        {t("Save")}
      </Button>
    </>
  );

  const mappingColumnFor = (
    sourceSystem: string,
  ): DataColumn<AllocationType> => ({
    header: <Typography variant="label">{t("Integration Map")}</Typography>,
    cell: (allocationType) => {
      const selected = selectedItems[allocationType.key]?.integrationEntity;
      // "none" is how the server spells "not mapped"; the DS resolves a label
      // by finding the id among the options, so it has to arrive as null.
      const value = selected && selected !== NOT_MAPPED ? selected : null;
      const options: SelectOption[] = integrationEntityList
        .filter(
          (entity) =>
            // A row keeps its own selection; the rest are hidden once taken.
            entity.entityKey === selected ||
            !selectedEntityKeys.has(entity.entityKey),
        )
        .map((entity) => ({
          id: entity.entityKey,
          label: t(entity.entityName, {
            nsSeparator: false,
            keySeparator: false,
          }),
        }));

      return (
        <Select
          size="md"
          aria-label={nameOf(allocationType)}
          placeholder={t("None")}
          options={options}
          noOptionsText={t("None")}
          value={value}
          onChange={(id) =>
            setSelectedItems((prev) => ({
              ...prev,
              [allocationType.key]: {
                ...prev[allocationType.key],
                integrationSourceSystem: sourceSystem,
                integrationEntity: id ?? NOT_MAPPED,
                // Unmapped means the tenant still edits it by hand.
                editable: !id,
              },
            }))
          }
        />
      );
    },
  });

  const columns: DataColumn<AllocationType>[] = [
    {
      header: <Typography variant="label">{t("Allocation Types")}</Typography>,
      cell: nameOf,
    },
    ...(integrationType && integrationEntityList.length > 0
      ? [mappingColumnFor(integrationType)]
      : []),
  ];

  const body = (
    <>
      <Typography variant="body2">{t("AllocationTypeMappingIntro")}</Typography>
      <DataTable<AllocationType>
        loading={loadingTypes}
        emptyState={t("No allocation types found")}
        columns={columns}
        rows={allocationTypes}
        getRowKey={(allocationType) => allocationType.key}
        aria-label={t("Allocation Types")}
      />
    </>
  );

  return (
    <>
      {/* The host drawer runs its body to the edges so the tab strip can,
          so the panel brings its own inset: the 20 the package's own drawer
          body uses, measured rather than picked. */}
      <Stack gap="var(--space-6)" sx={{ padding: "var(--space-6)" }}>
        {body}
      </Stack>
      {footerHost && createPortal(footer, footerHost)}
      {/* Overwriting the current allocations is what makes this the danger
          default rather than primary. */}
      <ConfirmDialog
        open={openWarningModal}
        title={t("Allocation Changes")}
        message={t("OverwriteAllocationChangesWarning")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        busy={loading}
        onConfirm={async () => {
          // Held open while the mutation runs, so the surface underneath is
          // not dismissable mid-save. The hook reports failure rather than
          // throwing, and closing on one would discard the draft the user
          // needs to retry with.
          const saved = await updateAllocationTypeSettings();
          setOpenWarningModal(false);
          if (saved) handleCloseDrawer();
        }}
        onCancel={() => {
          if (loading) return;
          setOpenWarningModal(false);
        }}
      />
    </>
  );
};

export default AllocationSettingsDrawer;
